import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from 'prop-types';

export const widths = {
  start: 260,
  min: 15,
  max: 600,
};

export const SidebarContainer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  flexGrow: 0,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "row",
  backgroundColor: theme.palette.background.default,
  position: "relative",
}));

export const SidebarContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDragging",
})(({ theme, isDragging }) => ({
  flexGrow: 1,
  flexShrink: 1,
  width: "var(--local-resizing-width, var(--local-initial-width))",
  overflow: "hidden",
  transition: isDragging ? "none" : `width ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeOut}`,
}));

// Avoid forwarding custom props to the DOM to prevent React warnings.
export const SidebarDivider = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDragging" && prop !== "isCollapsed",
})(({ theme, isDragging, isCollapsed }) => ({
  "--hit-size": "12px",
  "--line-width": "2px",
  width: "var(--hit-size)",
  cursor: "col-resize",
  flexGrow: 0,
  flexShrink: 0,
  position: "relative",
  userSelect: "none",
  touchAction: "none",
  marginLeft: isCollapsed ? "0px" : "-6px",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    bottom: 0,
    zIndex: 1,
    left: "calc((var(--hit-size) - var(--line-width)) / 2)",
    width: "var(--line-width)",
    background: isDragging ? theme.palette.primary.main : theme.palette.divider,
    transition: "background 0.2s ease",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    zIndex:0,
    top: 0,
    bottom: 0,
    left: "calc((var(--hit-size) - var(--line-width)) / 2)",
    width: "8px",
    background: theme.palette.background.paper,
    transition: "background 0.2s ease",
  },

  "&:hover::before": {
    background: theme.palette.primary.main,
  },

  "& .icon-container": {
    position: "absolute",
    zIndex: 2,
    top: "30px",
    left: "50%",
    transform: "translate(-50%, -50%)",
    pointerEvents: "auto",
    opacity: 1,
    transition: "opacity 0.2s ease",
    // Hover lives on the parent so the ring stays "highlighted" while the
    // cursor crosses between the ring SVG and the chevron SVG above it
    // (those are siblings — putting :hover on .regularCircleIcon alone
    // makes the ring snap back to divider mid-hover).
    '&:hover .regularCircleIcon': {
      color: theme.palette.primary.main,
    },
  },

  // The button stacks three FA icons on top of each other:
  //  1. circleIcon         — solid disc, fills the button's body so it covers
  //                          the divider line behind it. Must match the page
  //                          background in BOTH light and dark mode.
  //  2. regularCircleIcon  — outline ring drawn on top, gives the button its
  //                          subtle border. Must be a token that exists in
  //                          both admin and client palettes (palette.line was
  //                          admin-undefined → invisible ring on admin pages).
  //  3. angleIcon          — the chevron itself, primary-coloured for brand
  //                          affordance.
  "& .circleIcon": {
    fontSize: "1.25rem",
    transition: "color 0.2s ease",
    color: theme.palette.background.paper,
    '&:hover': {
      color: theme.palette.background.paper,
    },
  },

  "& .regularCircleIcon": {
    // Tied to `palette.divider` in both modes so the ring matches the
    // surrounding 1px dividers exactly.
    color: theme.palette.divider,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    transition: "color 0.2s ease",
  },

  "& .angleIcon": {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: "0.8rem",
    color: theme.palette.primary.main,
    transition: "color 0.2s ease",
  },
}));

export const HoverArea = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  width: 48,
  height: "100vh",
  zIndex: 9999,
  cursor: "pointer",
  [theme.breakpoints.down("lg")]: {
    width: 24,
  },
}));

export const NoPointerEvents = styled(Box)({
  pointerEvents: "none",
});

SidebarContainer.propTypes = {
  theme: PropTypes.shape({
    palette: PropTypes.any,
  }),
};

SidebarContent.propTypes = {
  theme: PropTypes.object,
  isDragging: PropTypes.bool,
};

SidebarDivider.propTypes = {
  theme: PropTypes.shape({
    palette: PropTypes.any,
  }),
  isDragging: PropTypes.bool,
  isCollapsed: PropTypes.bool,
};

HoverArea.propTypes = {
  theme: PropTypes.shape({
    breakpoints: PropTypes.any,
  }),
};
